import React from 'react'
import { NavLink } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { Home, LogOut, X, Users, ShieldCheck, Wallet, CreditCard, Ticket, Award, Landmark, Settings } from 'lucide-react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { redirectToSSOLogin } from '@/lib/sso'

interface AppSidebarProps {
  isOpen: boolean
  onClose: () => void
}

const navItems = [
  { label: 'Tổng quan', icon: Home, path: '/', end: true },
  { label: 'Người dùng', icon: Users, path: '/users', end: false },
  { label: 'Hồ sơ thuế', icon: ShieldCheck, path: '/tax-profiles', end: false },
  { label: 'Thanh toán thù lao', icon: Wallet, path: '/payouts', end: false },
  { label: 'Lịch sử thanh toán', icon: CreditCard, path: '/payments', end: false },
  { label: 'Danh sách Giftcode', icon: Ticket, path: '/gift-codes', end: false },
  { label: 'Cấu hình hệ thống', icon: Settings, path: '/system-config', end: false },
]

export const AppSidebar: React.FC<AppSidebarProps> = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth()

  const handleLogout = async () => {
    await logout()
    redirectToSSOLogin(window.location.origin)
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 flex flex-col
          transform transition-transform duration-200 ease-in-out
          lg:relative lg:translate-x-0 lg:z-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <a href="/" className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <Icon icon="ph:shield-check-fill" className="w-7 h-7 text-indigo-600" />
              <span className="text-lg font-bold text-gray-900">Admin Portal</span>
            </div>
            <span className="text-[11px] text-gray-500 pl-9 -mt-1">Hệ thống quản trị Hocnhe</span>
          </a>
          <button onClick={onClose} className="lg:hidden p-1 rounded-md hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                  isActive
                    ? 'bg-indigo-50 text-indigo-700 font-bold'
                    : 'text-gray-700 hover:bg-gray-100'
                }`
              }
            >
              <item.icon className="w-4 h-4 shrink-0" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-gray-200">
          {user && (
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              {user.avatar ? (
                <img
                  src={user.avatar}
                  alt="Avatar"
                  className="w-8 h-8 rounded-full object-cover"
                />
              ) : (
                <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-xs font-bold text-white">
                    {(user.display_name || user.email).charAt(0).toUpperCase()}
                  </span>
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-bold text-gray-900 truncate">
                  {user.display_name || user.email}
                </p>
                <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-2 rounded-lg text-xs font-bold text-red-650 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            Đăng xuất
          </button>
        </div>
      </aside>
    </>
  )
}
